import { trace, Tracer } from '@opentelemetry/api'

const SERVICE_NAME = 'hetzner-auction-ui-frontend'

// VITE_TRACING=otlp exports via the backend's /api/tempo proxy,
// VITE_TRACING=console logs spans to the browser console, unset = tracing off.
const MODE = import.meta.env.VITE_TRACING as string | undefined

let started = false

export function getTracer(): Tracer {
  return trace.getTracer(SERVICE_NAME)
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// The OTel SDK is heavy, so it is only loaded (async) when tracing is enabled.
async function start(mode: string): Promise<void> {
  const [
    { WebTracerProvider },
    { BatchSpanProcessor, SimpleSpanProcessor, ConsoleSpanExporter },
    { ZoneContextManager },
    { Resource },
    { ATTR_SERVICE_NAME },
    { registerInstrumentations },
    { XMLHttpRequestInstrumentation },
    { DocumentLoadInstrumentation },
    { UserInteractionInstrumentation },
  ] = await Promise.all([
    import('@opentelemetry/sdk-trace-web'),
    import('@opentelemetry/sdk-trace-base'),
    import('@opentelemetry/context-zone'),
    import('@opentelemetry/resources'),
    import('@opentelemetry/semantic-conventions'),
    import('@opentelemetry/instrumentation'),
    import('@opentelemetry/instrumentation-xml-http-request'),
    import('@opentelemetry/instrumentation-document-load'),
    import('@opentelemetry/instrumentation-user-interaction'),
  ])

  const spanProcessors = []
  if (mode === 'console') {
    spanProcessors.push(new SimpleSpanProcessor(new ConsoleSpanExporter()))
  } else {
    const { OTLPTraceExporter } = await import('@opentelemetry/exporter-trace-otlp-http')
    spanProcessors.push(
      new BatchSpanProcessor(
        new OTLPTraceExporter({ url: `${window.location.origin}/api/tempo/v1/traces` })
      )
    )
  }

  const provider = new WebTracerProvider({
    resource: new Resource({ [ATTR_SERVICE_NAME]: SERVICE_NAME }),
    spanProcessors,
  })

  provider.register({ contextManager: new ZoneContextManager() })

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new UserInteractionInstrumentation(),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: [
          new RegExp(`^${escapeRegExp(window.location.origin)}`),
        ],
      }),
    ],
  })
}

export function setupTracing(): void {
  if (started || (MODE !== 'otlp' && MODE !== 'console')) return
  started = true
  start(MODE).catch((err) => console.warn('Tracing setup failed:', err))
}
